import React, { useState } from 'react';
import { processBillPayment } from '../../utils/firestore';
import { useAuth } from '../../contexts/AuthContext';

const UTILITY_PROVIDERS = [
  {
    name: 'British Gas',
    category: 'Utilities',
    accountFormat: '9999999999',
    website: 'www.britishgas.co.uk'
  },
  {
    name: 'BT Group',
    category: 'Phone/Broadband',
    accountFormat: '999999999',
    website: 'www.bt.com'
  },
  {
    name: 'Virgin Media',
    category: 'Cable/Broadband',
    accountFormat: '999999999',
    website: 'www.virginmedia.com'
  },
  {
    name: 'Thames Water',
    category: 'Utilities',
    accountFormat: '9999999999',
    website: 'www.thameswater.co.uk'
  },
  {
    name: 'Aviva',
    category: 'Insurance',
    accountFormat: '999-9999-999',
    website: 'www.aviva.co.uk'
  }
];

export default function BillPay({ accounts }) {
  const { currentUser } = useAuth();
  const [fromAccount, setFromAccount] = useState('');
  const [provider, setProvider] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Get tomorrow's date as minimum date for scheduling
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const commonBills = [
    { name: 'British Gas', icon: '⚡', provider: 'British Gas' },
    { name: 'Thames Water', icon: '💧', provider: 'Thames Water' },
    { name: 'Virgin Media', icon: '🌐', provider: 'Virgin Media' },
    { name: 'EE', icon: '📱', provider: 'EE Mobile' },
    { name: 'Aviva', icon: '🛡️', provider: 'Aviva' },
    { name: 'Netflix', icon: '🎬', provider: 'Netflix' }
  ];

  const dateStringDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
  };

  const upcomingBills = [
    {
      provider: 'British Gas',
      shortName: 'British Gas',
      icon: '⚡',
      amount: 84.99,
      dueIn: 3,
      accountNumber: '****2345'
    },
    {
      provider: 'Thames Water',
      shortName: 'Thames Water',
      icon: '💧',
      amount: 45.50,
      dueIn: 5,
      accountNumber: '****7890'
    },
    {
      provider: 'Virgin Media',
      shortName: 'Virgin Media',
      icon: '🌐',
      amount: 79.99,
      dueIn: 7,
      accountNumber: '****4321'
    }
  ];

  const paymentHistory = [
    {
      provider: 'British Gas',
      shortName: 'British Gas',
      amount: 84.99,
      date: dateStringDaysAgo(14),
      status: 'Paid',
      confirmationNumber: 'BG123456'
    },
    {
      provider: 'EE Mobile',
      shortName: 'EE',
      amount: 75.00,
      date: dateStringDaysAgo(7),
      status: 'Paid',
      confirmationNumber: 'EE789012'
    },
    {
      provider: 'Virgin Media',
      shortName: 'Virgin Media',
      amount: 79.99,
      date: dateStringDaysAgo(21),
      status: 'Paid',
      confirmationNumber: 'XFN345678'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!fromAccount || !provider || !accountNumber || !amount || !scheduleDate) {
      setError('Please fill in all required fields');
      return;
    }

    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const selectedAccount = accounts.find(acc => acc.type === fromAccount);
    if (paymentAmount > selectedAccount.available) {
      setError('Insufficient funds');
      return;
    }

    setLoading(true);
    try {
      await processBillPayment({
        fromAccount,
        provider,
        accountNumber,
        amount: paymentAmount,
        scheduleDate,
        memo,
        userId: currentUser.uid
      });

      // Reset form
      setProvider('');
      setAccountNumber('');
      setAmount('');
      setScheduleDate('');
      setMemo('');
      setSuccess(true);
    } catch (error) {
      setError('Payment failed. Please try again.');
      console.error('Bill payment error:', error);
    }
    setLoading(false);
  };

  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1';
  const fieldStyles = 'w-full p-2 border border-gray-300 dark:border-slate-600 rounded focus:ring-primary-500 focus:border-primary-500 bg-white dark:!bg-slate-700 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400';

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bills</h3>
      
      {success && (
        <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded">
          Payment scheduled successfully!
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-300 rounded">
                {error}
              </div>
            )}

            <div>
              <label className={labelClass}>
                From Account
              </label>
              <select
                value={fromAccount}
                onChange={(e) => setFromAccount(e.target.value)}
                className={fieldStyles}
                disabled={loading}
              >
                <option value="">Select account</option>
                {accounts.map((account) => (
                  <option key={account.accountNumber} value={account.type}>
                    {account.type} - {formatCurrency(account.available)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Common Providers
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {commonBills.map((bill) => (
                  <button
                    key={bill.name}
                    type="button"
                    onClick={() => setProvider(bill.provider)}
                    className={`p-3 rounded-xl text-sm font-medium transition-all duration-200 flex flex-col items-center justify-center
                      ${provider === bill.provider 
                        ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-slate-800' 
                        : 'bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'}`}
                  >
                    <span className="text-xl mb-1">{bill.icon}</span>
                    <span className="text-xs text-center">{bill.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>
                Account Number
              </label>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="Enter account number"
                className={fieldStyles}
                disabled={loading}
              />
            </div>

            <div>
              <label className={labelClass}>
                Amount
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className={fieldStyles}
                disabled={loading}
                min="0.01"
                step="0.01"
              />
            </div>

            <div>
              <label className={labelClass}>
                Payment Date
              </label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className={fieldStyles}
                disabled={loading}
                min={minDate}
              />
            </div>

            <div>
              <label className={labelClass}>
                Memo (Optional)
              </label>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="Add a memo"
                className={fieldStyles}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={loading}
            >
              {loading ? 'Processing…' : 'Schedule payment'}
            </button>
          </form>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-6 border border-transparent dark:border-slate-600">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Upcoming Bills</h3>
            <div className="space-y-3">
              {upcomingBills.map((bill, index) => (
                <div key={index} className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border border-slate-200/80 dark:border-slate-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                        <span className="text-lg">{bill.icon}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{bill.provider}</p>
                        <div className="flex items-center text-sm space-x-2">
                          <span className="text-emerald-600 dark:text-emerald-400">Due in {bill.dueIn} days</span>
                          <span className="text-gray-400 dark:text-slate-500">•</span>
                          <span className="text-gray-500 dark:text-slate-400">Acct: {bill.accountNumber}</span>
                        </div>
                      </div>
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white">${bill.amount.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-6 border border-transparent dark:border-slate-600">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Payment History</h3>
            <div className="space-y-3">
              {paymentHistory.map((payment, index) => (
                <div key={index} className="flex items-center justify-between text-sm p-2 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-colors duration-200">
                  <div className="flex flex-col">
                    <span className="text-gray-900 dark:text-slate-100 font-medium">{payment.provider}</span>
                    <span className="text-gray-500 dark:text-slate-400 text-xs">
                      Conf: {payment.confirmationNumber}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-900 dark:text-slate-100">${payment.amount.toFixed(2)}</span>
                    <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-full text-xs">
                      {payment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 